import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Train } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/general`, formData);
      toast.success('Message envoyé avec succès !', {
        description: 'Nous vous répondrons dans les plus brefs délais.'
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
            Contact
          </span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
            Contactez-nous
          </h1>
          <div className="w-16 h-0.5 bg-[#C8A75A] mx-auto mb-6"></div>
          <p className="text-[#F5F5F5]/90 text-lg max-w-2xl mx-auto">
            Une question sur la formation, les inscriptions ou les partenariats ? 
            Notre équipe est à votre disposition.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-24 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-8">
                Nos coordonnées
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
                  <MapPin className="text-[#C8A75A] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">Adresse</h3>
                    <p className="text-[#4A4A4A] text-sm">
                      Institut BIA<br />
                      Saint-Denis (93)<br />
                      Île-de-France
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
                  <Train className="text-[#C8A75A] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">Accès</h3>
                    <p className="text-[#4A4A4A] text-sm">
                      Métro Ligne 13<br />
                      À proximité du centre
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
                  <Mail className="text-[#C8A75A] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">Email</h3>
                    <div className="space-y-1">
                      <a href="mailto:contact@institut-bia.fr" className="block text-[#4A4A4A] text-sm hover:text-[#C8A75A] transition-colors">
                        contact@institut-bia.fr
                      </a>
                      <a href="mailto:partenaires@institut-bia.fr" className="block text-[#4A4A4A] text-sm hover:text-[#C8A75A] transition-colors">
                        partenaires@institut-bia.fr
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
                  <Phone className="text-[#C8A75A] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">Téléphone</h3>
                    <p className="text-[#4A4A4A] text-sm">
                      +33 1 87 66 30 91
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
                  <Clock className="text-[#C8A75A] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">Horaires</h3>
                    <p className="text-[#4A4A4A] text-sm">
                      Lundi - Vendredi<br />
                      9h00 - 18h00
                    </p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="mt-8 p-6 bg-[#0B1F3A] text-white">
                <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4">
                  Certifications
                </h3>
                <ul className="space-y-2 text-sm text-[#F5F5F5]/80">
                  <li>• CFA déclaré DREETS Île-de-France</li>
                  <li>• RNCP39063 Niveau 5</li>
                  <li>• Qualiopi en cours</li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-8">
                Envoyez-nous un message
              </h2>

              <form onSubmit={handleSubmit} className="bg-[#F5F5F5] p-8 md:p-12 border border-[#E6E6E6]" data-testid="general-contact-form">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                      Nom complet *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                      data-testid="input-contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="subject" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Sujet *
                  </Label>
                  <Select onValueChange={handleSelectChange} value={formData.subject}>
                    <SelectTrigger className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]" data-testid="select-contact-subject">
                      <SelectValue placeholder="Sélectionnez un sujet" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="formation">Question sur la formation</SelectItem>
                      <SelectItem value="inscription">Inscription / Candidature</SelectItem>
                      <SelectItem value="partenariat">Partenariat entreprise</SelectItem>
                      <SelectItem value="autre">Autre demande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mb-8">
                  <Label htmlFor="message" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Décrivez votre demande..."
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="textarea-contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none py-6 text-base font-semibold"
                  data-testid="submit-contact"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="bg-[#F5F5F5]" data-testid="map-section">
        <div className="h-[400px] bg-[#E6E6E6] flex items-center justify-center">
          <div className="text-center">
            <MapPin className="mx-auto text-[#C8A75A] mb-4" size={48} />
            <p className="text-[#4A4A4A]">
              Institut BIA - Saint-Denis (93)<br />
              <span className="text-sm">Accessible Métro Ligne 13</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
